import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronRightIcon } from '../../../icons/chevron-right';
import { DotIcon } from '../../../icons/dot';
import { cn } from '../../../lib/utils';

/**
 * Status row of a section in a review index (Figma "Resume section item"):
 * title, a coloured status label, an optional "· Opcional" suffix and a
 * chevron. The whole row is one button so the touch target is the row.
 *
 * Domain-agnostic: the labels come from the caller; `tone` only picks the
 * colour of the status label.
 */
const statusVariants = cva('text-xs font-medium leading-none whitespace-nowrap', {
  variants: {
    tone: {
      /** Everything required is filled and valid. */
      complete: 'text-primary',
      /** Has content, but a required field is blank or invalid. */
      pending: 'text-tone-red',
      /** Nothing filled yet. */
      empty: 'text-muted-foreground',
    },
  },
  defaultVariants: { tone: 'empty' },
});

export type ResumeSectionItemTone = NonNullable<VariantProps<typeof statusVariants>['tone']>;

export interface ResumeSectionItemProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'title' | 'children'
> {
  /** Section name. */
  title: ReactNode;
  /** Status text (e.g. "Preenchido"). Colour follows `tone`. */
  statusLabel: ReactNode;
  tone: ResumeSectionItemTone;
  /** Appends "· <optionalLabel>" after the status. */
  optional?: boolean;
  /** @default 'Opcional' */
  optionalLabel?: string;
  /** Root testid. The row is the button, so there is nothing else to derive. */
  'data-testid'?: string;
}

const ResumeSectionItem = forwardRef<HTMLButtonElement, ResumeSectionItemProps>(
  (
    { title, statusLabel, tone, optional = false, optionalLabel = 'Opcional', className, ...props },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      data-slot="resume-section-item"
      data-tone={tone}
      className={cn(
        // The content is 32px tall (14 title + 6 gap + 12 status); the vertical
        // padding is cancelled by the negative margin so the row keeps the Figma
        // rhythm while the hit area reaches 44px.
        'flex w-full items-center gap-4 py-1.5 -my-1.5 text-left',
        'rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      <span data-slot="resume-section-item-body" className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span
          data-slot="resume-section-item-title"
          className="break-words text-sm font-medium leading-none text-accent-foreground"
        >
          {title}
        </span>
        <span data-slot="resume-section-item-status" className="flex items-center">
          <span className={statusVariants({ tone })}>{statusLabel}</span>
          {optional && (
            <>
              <DotIcon className="size-3 text-muted-foreground" />
              <span
                data-slot="resume-section-item-optional"
                className="text-xs leading-none whitespace-nowrap text-muted-foreground"
              >
                {optionalLabel}
              </span>
            </>
          )}
        </span>
      </span>
      <ChevronRightIcon className="size-4 shrink-0 text-primary" />
    </button>
  )
);
ResumeSectionItem.displayName = 'ResumeSectionItem';

export { ResumeSectionItem, statusVariants as resumeSectionItemStatusVariants };
